<template>
  <div v-if="activeBanners.length" class="banner-slide-wrapper">
    <div ref="sliderRef" class="keen-slider banner-carousel">
      <div
        v-for="(banner, index) in activeBanners"
        :key="`banner-${index}`"
        class="keen-slider__slide banner-slide"
      >
        <div class="banner-image-container">
          <img
            v-if="shouldLoadImage(index)"
            :src="banner.url"
            :alt="banner.alt"
            class="banner-image"
            :class="{ loaded: imageLoaded[index] }"
            :loading="index === 0 ? 'eager' : 'lazy'"
            :fetchpriority="index === 0 ? 'high' : 'auto'"
            decoding="async"
            @load="handleImageLoad(index)"
            @error="handleImageError(index)"
          />
          <div v-if="!imageLoaded[index]" class="image-loading-overlay">
            <div class="loading-spinner" />
          </div>
          <a
            v-if="banner.link"
            :href="banner.link"
            :aria-label="banner.alt"
            class="banner-link-overlay"
            draggable="false"
            @click="handleBannerClick"
          />
        </div>
      </div>
    </div>

    <!-- Custom Indicators -->
    <div
      v-if="showIndicators && activeBanners.length > 1"
      class="carousel-indicators"
    >
      <button
        v-for="(banner, index) in activeBanners"
        :key="`indicator-${index}`"
        :class="{ active: currentSlide === index }"
        :aria-label="`Slide ${index + 1}`"
        @click="goToSlide(index)"
      />
    </div>
  </div>
</template>

<script>
import KeenSlider from 'keen-slider'
import getCurrentDomain from '@/utils/domain'

export default {
  name: 'BannerSlide',
  props: {
    autoPlay: {
      type: Boolean,
      default: true
    },
    interval: {
      type: Number,
      default: 5000
    },
    showIndicators: {
      type: Boolean,
      default: true
    },
    customBanners: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      slider: null,
      currentSlide: 0,
      imageLoaded: {},
      isTouchDevice: false,
      autoplayCleanup: null,
      loading: false,
      suppressClick: false,
      banners: []
    }
  },
  computed: {
    activeBanners() {
      return this.customBanners || this.banners
    }
  },
  async mounted() {
    this.isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0

    if (this.customBanners && this.customBanners.length) {
      this.initializeImageLoading()
      this.$nextTick(() => this.initKeenSlider())
      return
    }

    await this.fetchBanners()

    if (!this.banners.length) return

    this.initializeImageLoading()
    this.$nextTick(() => this.initKeenSlider())
  },
  beforeDestroy() {
    // Clean up autoplay
    if (this.autoplayCleanup) {
      this.autoplayCleanup()
      this.autoplayCleanup = null
    }

    // Destroy slider
    if (this.slider) {
      this.slider.destroy()
      this.slider = null
    }
  },
  methods: {
    async fetchBanners() {
      this.loading = true
      try {
        const response = await this.$http.post('/internal/banners', {
          domain: getCurrentDomain()
        })
        if (response.data.success && Array.isArray(response.data.data)) {
          this.banners = response.data.data.map(b => ({
            url: b.image_url,
            alt: b.title,
            link: b.link_url || null
          }))
        }
      } catch (error) {
        console.error('Failed to fetch banners:', error)
      } finally {
        this.loading = false
      }
    },

    initializeImageLoading() {
      this.activeBanners.forEach((_, index) => {
        this.$set(this.imageLoaded, index, false)
      })
    },

    // Progressive lazy loading - load current, next, and previous slides
    shouldLoadImage(index) {
      if (index === 0) return true // Always load first image
      if (this.activeBanners.length <= 3) return true // Load all if 3 or fewer

      const current = this.currentSlide
      const total = this.activeBanners.length
      const prev = current === 0 ? total - 1 : current - 1
      const next = (current + 1) % total

      return index === current || index === prev || index === next
    },

    handleImageLoad(index) {
      this.$set(this.imageLoaded, index, true)
    },

    handleImageError(index) {
      this.$set(this.imageLoaded, index, false)
      console.warn(`Failed to load banner image at index ${index}`)
    },

    handleBannerClick(event) {
      // A click that follows a drag/swipe must not navigate
      if (this.suppressClick) {
        event.preventDefault()
        this.suppressClick = false
      }
    },

    goToSlide(index) {
      if (this.slider) {
        this.slider.moveToIdx(index)
      }
    },

    initKeenSlider() {
      if (!this.$refs.sliderRef || !this.activeBanners.length) {
        return
      }

      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

      // Configure Keen Slider options
      const options = {
        loop: true,
        mode: 'snap',
        slides: {
          perView: 1,
          spacing: 0
        },
        // Disable animations if reduced motion is preferred
        duration: prefersReducedMotion ? 0 : 300,
        // Optimize for performance
        renderMode: 'performance',
        drag: true
      }

      // Create plugins array
      const plugins = []

      // Add autoplay plugin if enabled
      if (
        this.autoPlay &&
        !prefersReducedMotion &&
        this.activeBanners.length > 1
      ) {
        // Store component context
        const componentInterval = this.interval
        const componentIsTouchDevice = this.isTouchDevice
        const componentContext = this

        const autoplayPlugin = slider => {
          let timeout
          let mouseOver = false

          function clearNextTimeout() {
            clearTimeout(timeout)
          }

          function nextTimeout() {
            clearTimeout(timeout)
            if (mouseOver) return
            timeout = setTimeout(() => {
              slider.next()
            }, componentInterval)
          }

          slider.on('created', () => {
            // Pause on hover for non-touch devices
            if (!componentIsTouchDevice) {
              slider.container.addEventListener('mouseover', () => {
                mouseOver = true
                clearNextTimeout()
              })
              slider.container.addEventListener('mouseout', () => {
                mouseOver = false
                nextTimeout()
              })
            }

            // Pause on touch
            slider.container.addEventListener('touchstart', () => {
              mouseOver = true
              clearNextTimeout()
            })
            slider.container.addEventListener('touchend', () => {
              mouseOver = false
              setTimeout(() => nextTimeout(), 1000)
            })

            nextTimeout()
          })

          slider.on('dragStarted', clearNextTimeout)
          slider.on('animationEnded', nextTimeout)
          slider.on('updated', nextTimeout)

          componentContext.autoplayCleanup = clearNextTimeout
        }

        plugins.push(autoplayPlugin)
      }

      // Initialize slider
      try {
        this.slider = new KeenSlider(this.$refs.sliderRef, options, plugins)

        // Track slide changes
        this.slider.on('slideChanged', () => {
          this.currentSlide = this.slider.track.details.rel
          this.$emit('slide-change', this.currentSlide)
        })

        // dragChecked only fires once real movement is detected, so a swipe
        // arms suppressClick and the click on release is swallowed, while a
        // clean click (no movement) navigates normally.
        this.slider.on('dragStarted', () => {
          this.suppressClick = false
        })
        this.slider.on('dragChecked', () => {
          this.suppressClick = true
        })
      } catch (error) {
        console.error('Keen Slider: Failed to initialize:', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Fixed dark theme colors
$bg-card: #1a1a2e;
$bg-elevated: #16213e;

// Performance optimization: Layout containment
.banner-slide-wrapper {
  width: 100%;
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  contain: layout style paint;

  @media (max-width: 768px) {
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 480px) {
    border-radius: 10px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  }
}

// Keen Slider base styles
.banner-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: linear-gradient(45deg, $bg-card, $bg-elevated);

  // Performance optimizations
  &.keen-slider {
    display: flex;
    user-select: none;
    -webkit-user-select: none;
    touch-action: pan-y;
  }
}

// Individual slide styles
.keen-slider__slide {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  height: auto;
}

.banner-slide {
  position: relative;
  width: 100%;
  // Use padding-bottom for aspect ratio
  height: 0;
  padding-bottom: 22.5%; // 3:1 aspect ratio (reduced by 10% from original)

  @media (max-width: 768px) {
    padding-bottom: 27%; // Slightly taller on mobile (same as original)
  }

  @media (max-width: 480px) {
    padding-bottom: 30%; // Even taller on small mobile
  }
}

.banner-image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(45deg, $bg-card, $bg-elevated);
}

// Optimized image styles - removed expensive filters
.banner-image {
  width: 100%;
  height: 100%;
  object-fit: fill;
  object-position: center;
  opacity: 0;
  transform: translate3d(0, 0, 0);
  transition: opacity 0.3s ease;

  // Light darkening effect without filter (better performance)
  filter: brightness(0.92);

  &.loaded {
    opacity: 1;
  }

  // Disable hover effects on touch devices for better performance
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      filter: brightness(0.98);
    }
  }
}

// Click-through overlay (only rendered when the banner has a link) — sits
// above the image and loading overlay so the whole slide is the link target
.banner-link-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
}

// Simplified loading overlay
.image-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, $bg-card, $bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

// Lightweight spinner animation
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-width: 2px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Custom optimized indicators
.carousel-indicators {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
  padding: 0;
  margin: 0;

  button {
    width: 10px;
    height: 10px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    outline: none;

    // Remove tap highlight on mobile
    -webkit-tap-highlight-color: transparent;

    // Hover effect only on non-touch devices
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: rgba(255, 255, 255, 0.7);
        transform: scale(1.1);
      }
    }

    &.active {
      background: #f5deb3;
      border-color: #f5deb3;
      transform: scale(1.25);
      box-shadow: 0 2px 10px rgba(245, 222, 179, 0.4);
    }

    // Touch feedback
    &:active {
      transform: scale(0.95);
    }
  }

  @media (max-width: 768px) {
    bottom: 10px;
    gap: 6px;

    button {
      width: 8px;
      height: 8px;

      &.active {
        transform: scale(1.2);
      }
    }
  }

  @media (max-width: 480px) {
    bottom: 8px;
    gap: 5px;

    button {
      width: 7px;
      height: 7px;
      border-width: 1px;

      &.active {
        transform: scale(1.15);
      }
    }
  }
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
  .banner-image,
  .carousel-indicators button {
    transition: none;
  }

  .loading-spinner {
    animation: none;
    opacity: 0.5;
  }
}
</style>
